import {nodeAddedMap} from './MutationHandler';

const instances = new WeakMap();
const selector = '[data-controller="l10n-selector"]';

nodeAddedMap.set(selector, (target) => {
  if (!target.matches(selector)) {
    return;
  }

  if (!instances.get(target)) {
    const instance = new L10nController(target);
    instance.initialize();
    instances.set(target, instance);
  }
});

export class L10nController {
  element: Element;
  labelTarget: Element;
  dropdownTarget: HTMLSelectElement;

  constructor(element: Element) {
    this.element = element;
    const labelTarget = element.querySelector(
      '[data-target="l10n-selector.label"]',
    );
    if (labelTarget === null) {
      throw new Error('Missing [data-target="l10n-selector.label"]');
    }
    this.labelTarget = labelTarget;
    const dropdown = element.querySelector(
      '[data-target="l10n-selector.dropdown"]',
    );
    if (dropdown === null) {
      throw new Error('Missing [data-target="l10n-selector.dropdown"]');
    }

    this.dropdownTarget = dropdown as HTMLSelectElement;
    this.dropdownTarget.addEventListener('change', (event) => {
      event.preventDefault();
      // Get the necessary info from the event and the selected option element
      let selectedLanguage = this.dropdownTarget.value;
      let selectedOptionEl = this.dropdownTarget.selectedOptions[0];
      let label = selectedOptionEl.dataset.languageDisplay || '';
      let url = selectedOptionEl.dataset.url;

      if (!url) {
        throw new Error('Invalid language selection');
      }
      let external =
        selectedOptionEl.dataset.external === 'true' ? true : false;

      // Update the label display
      this.setLanguageLabel(label);
      // Store the user's preference in a cookie for reference later
      if (!external) {
        this.saveUserPreference(selectedLanguage);
      }
      // Redirect to the language selected by the user
      window.location.href = url;
    });
  }

  initialize() {
    let alternates = this.getAlternates() || [];
    let sites = this.getSites() || [];

    this.renderInitialState(alternates, sites);
    this.element.classList.remove('hidden');
  }

  getAlternates(): hudl.l10n.Alternate[] | false {
    if (window.hudl && window.hudl.l10n && window.hudl.l10n.alternates) {
      return window.hudl.l10n.alternates;
    }
    return false;
  }

  getSites(): hudl.l10n.Alternate[] | false {
    if (window.hudl && window.hudl.l10n && window.hudl.l10n.sites) {
      return window.hudl.l10n.sites;
    }
    return false;
  }

  /**
   * Render the initial state of this component, primarily composed of a
   * select dropdown with available options presented to the visitor.
   *
   * @param alternates Array of alternate language versions of this page
   * @param sites Array of supported sites
   */
  renderInitialState(
    alternates: hudl.l10n.Alternate[],
    sites: hudl.l10n.Alternate[],
  ) {
    const optionsData: Record<string, hudl.l10n.Alternate> = {};
    for (const alternate of sites || []) {
      optionsData[alternate.language] = alternate;
    }
    for (const alternate of alternates || []) {
      optionsData[alternate.language] = alternate;
    }

    Object.values(optionsData).forEach((alternate) => {
      if (alternate.current === true) {
        // Populate the current language
        this.setLanguageLabel(alternate.languageDisplay);
      }

      // create the select option
      let option = document.createElement('option');
      option.value = alternate.language;
      option.text = alternate.languageDisplay;
      option.dataset.external = alternate.external ? 'true' : 'false';

      // `data-target` is used by Stimulus to quickly retrieve these elements
      option.dataset.target = 'l10n-selector.option';

      // `data-url` and `data-language-display` will be used to update the
      // UI on change
      if (alternate.external) {
        var params = new URLSearchParams({
          utm_source: 'wwwhudlcom',
          utm_medium: 'web',
          utm_campaign: 'languageSelect',
        });
        alternate.url = `${alternate.url}?${params.toString()}`;
      }
      option.dataset.url = alternate.url;
      option.dataset.languageDisplay = alternate.languageDisplay;
      option.setAttribute('translate', 'no');

      if (alternate.current) {
        // Mark the current locale as `selected`
        option.setAttribute('selected', '');
      }

      // We use native-language labels, so set the current language indicator
      // to translate=no to prevent tools like Google Translate from changing
      // the predefined labels.
      this.labelTarget.setAttribute('translate', 'no');

      // Add the option to the select
      this.dropdownTarget.add(option, null);
    });
  }

  setLanguageLabel(languageDisplay: string) {
    this.labelTarget.innerHTML = languageDisplay + ' ▾';
  }

  saveUserPreference(language: string) {
    if (
      'hasConsent' in window &&
      typeof window.hasConsent === 'function' &&
      window.hasConsent('functional')
    ) {
      const expire = new Date();
      expire.setDate(expire.getDate() + 365);
      document.cookie = `mktgLanguagePref=${language};expires=${expire.toUTCString()}`;
    }
  }
}
